<template>
  <div>
    <v-container fluid>
      <!-- Form Content Card -->
      <v-card class="setup-form-card" elevation="0">
        <div class="header-text-content">
          <h3 class="setup-title">{{ $t("_SetupUserGroup") }}</h3>
          <p class="setup-subtitle">{{ $t('_createUserGroup') }}</p>
        </div>
        <v-card-text class="form-content">
          <!-- Basic Information Section -->
          <div class="border-section-wrapper mb-7">
            <div class="basic-info-container">
              <img :src="images.basicInfoIcon" alt="Basic Info" class="basic-info-icon" />
              <span class="section-title">{{ $t('basicInformation') }}</span>
            </div>

            <v-row class="section-content">
              <!-- Group Name -->
              <v-col cols="12" md="6">
                <div class="form-field">
                  <span class="input-label">
                    {{ $t('groupname') }}<span class="required-asterisk">*</span>
                    <v-tooltip location="top" max-width="300">
                      <template v-slot:activator="{ props }">
                        <v-icon v-bind="props" size="16" class="info-icon">
                          mdi-information-outline
                        </v-icon>
                      </template>
                      <div class="tooltip-content">
                        <div>{{ $t('_enterDescriptiveGroupName') }}</div>
                      </div>
                    </v-tooltip>
                  </span>
                  <v-text-field id="groupname-setupusergroup" :label="$t('groupname')" variant="outlined"
                    density="comfortable" hide-details="auto" v-model="groupname" :placeholder="$t('_enterGroupName')"
                    class="modern-field mt-2" :rules="[v => !!v || $t('_groupNameRequired')]"></v-text-field>
                </div>
              </v-col>

              <!-- Group ID (Object ID) -->
              <v-col cols="12" md="6">
                <div class="form-field">
                  <span class="input-label">
                    {{ $t('groupObjectID') }}<span class="required-asterisk">*</span>
                    <v-tooltip location="top" max-width="300">
                      <template v-slot:activator="{ props }">
                        <v-icon v-bind="props" size="16" class="info-icon">
                          mdi-information-outline
                        </v-icon>
                      </template>
                      <div class="tooltip-content">
                        <div>{{ $t('_uniqueSystemCode') }}</div>
                      </div>
                    </v-tooltip>
                  </span>
                  <v-text-field id="groupobjectid-setupusergroup" :label="$t('groupObjectID')" variant="outlined"
                    density="comfortable" hide-details="auto" v-model="groupObjectID" :placeholder="$t('_enterGroupId')"
                    class="modern-field mt-2"></v-text-field>
                </div>
              </v-col>
            </v-row>
          </div>

          <!-- Model Configuration Section -->
          <div class="border-section-wrapper">
            <div class="basic-info-container">
              <img :src="images.modelConfigIcon" alt="Model Config" class="basic-info-icon" />
              <h4 class="section-title">{{ $t('modelConfiguration') }}</h4>
            </div>

            <v-row class="section-content">
              <!-- Model Grouping -->
              <v-col cols="12" md="6">
                <div class="form-field">
                  <span class="input-label">
                    {{ $t('selectModelGrouping') }}
                    <v-tooltip location="top start" max-width="300">
                      <template v-slot:activator="{ props }">
                        <v-icon v-bind="props" size="16" class="info-icon">
                          mdi-information-outline
                        </v-icon>
                      </template>
                      <div class="tooltip-content">
                        <div>{{ $t('_selectModelGroupingCategory') }}</div>
                      </div>
                    </v-tooltip>
                  </span>
                  <v-autocomplete id="modelgrouping-setupusergroup" v-model:search="searchGroupModel"
                    :items="listDDGroupModel || []" item-title="name" item-value="id" clearable variant="outlined"
                    hide-details="auto" v-model="groupModelID" density="comfortable" :placeholder="$t('selectModelGrouping')"
                    :label="$t('modelGrouping')" class="modern-field mt-2"
                    :rules="[v => !!v || $t('_modelGroupingRequired')]">
                    <template v-slot:item="{ props, item, index }">
                      <v-list-item v-bind="props" :id="'modelgrouping-setupusergroup-option-' + index"
                        :value="item.raw.name">
                        <template v-slot:title>
                          <span>{{ item.raw.name }}</span>
                        </template>
                      </v-list-item>
                    </template>
                  </v-autocomplete>
                </div>
              </v-col>

              <!-- Model Selection -->
              <v-col cols="12" md="6">
                <div class="form-field">
                  <span class="input-label">
                    {{ $t('selectModel') }}
                    <v-tooltip location="top" max-width="300">
                      <template v-slot:activator="{ props }">
                        <v-icon v-bind="props" size="16" class="info-icon">
                          mdi-information-outline
                        </v-icon>
                      </template>
                      <div class="tooltip-content">
                        <div>{{ $t('_selectWhichAiModel') }}</div>
                      </div>
                    </v-tooltip>
                  </span>
                  <v-autocomplete id="models-setupusergroup" :items="listDDModel || []" item-title="name"
                    item-value="modelDisplayID" multiple hide-details="auto" variant="outlined" clearable
                    v-model="modelDisplay" density="comfortable" :placeholder="$t('_selectModels')" :label="$t('model')"
                    class="modern-field mt-2 model-autocomplete"
                    :rules="[v => (v && v.length > 0) || $t('_atLeastOneModelRequired')]"
                    :menu-props="{ closeOnContentClick: false, closeOnOutsideClick: false }">
                    <template #prepend-item>
                      <v-list-item class="model-option-item model-all-option"
                        :class="{ 'model-all-option--active': isAllModelsSelected(modelDisplay), 'model-all-option--disabled': !listDDModel || !listDDModel.length }"
                        density="compact" @mousedown.prevent @click.stop="toggleAllModels">
                        <template #prepend>
                          <v-checkbox-btn class="model-option-checkbox"
                            :model-value="isAllModelsSelected(modelDisplay)" color="#C30000" :ripple="false"
                            :disabled="!listDDModel || !listDDModel.length" @click.stop="toggleAllModels"></v-checkbox-btn>
                        </template>
                        <template #title>
                          <div class="model-all-title">
                            <span class="model-all-option__label">{{ $t('allModel') }}</span>
                            <v-tooltip location="top">
                              <template #activator="{ props }">
                                <v-icon v-bind="props" size="18" class="model-info-icon">
                                  mdi-information-outline
                                </v-icon>
                              </template>
                              <span>{{ $t('_allAvailableModelsWillBeIncluded') }}</span>
                            </v-tooltip>
                          </div>
                        </template>
                      </v-list-item>
                      <v-divider class="model-all-divider"></v-divider>
                    </template>

                    <template v-slot:item="{ props, item, index }">
                      <v-list-item v-bind="props" :id="'models-setupusergroup-option-' + index"
                        class="model-option-item"
                        :class="{ 'model-option-item--selected': isModelSelected(item.raw.modelDisplayID) }"
                        @click="props.onClick" @mousedown.prevent>
                        <template #prepend>
                          <v-checkbox-btn class="model-option-checkbox"
                            :model-value="isModelSelected(item.raw.modelDisplayID)" color="#C30000" :ripple="false"
                            @click.stop="props.onClick"></v-checkbox-btn>
                        </template>
                        <template v-slot:title>
                          <span class="model-option-title">{{ item.raw.name }}</span>
                        </template>
                      </v-list-item>
                    </template>

                    <template #selection="{ item, index }">
                      <span v-if="shouldCollapseModelChips(modelDisplay) && index === 0"
                        class="model-all-selected-label">
                        {{ $t('allModel') }}
                        <v-tooltip location="top">
                          <template #activator="{ props }">
                            <v-icon v-bind="props" size="18" class="model-info-icon">
                              mdi-information-outline
                            </v-icon>
                          </template>
                          <span>{{ $t('_allAvailableModelsSelected') }}</span>
                        </v-tooltip>
                      </span>
                      <template v-else-if="!shouldCollapseModelChips(modelDisplay)">
                        <v-chip v-if="index < 3" class="model-chip" size="small" closable
                          @click:close="removeModel(item.value)">
                          <template #close>x</template>
                          {{ item.title }}
                        </v-chip>
                        <v-chip v-else-if="index === 3" class="model-chip more-chip" size="small">
                          +{{ modelDisplay.length - 3 }} {{ $t('more') }}
                        </v-chip>
                      </template>
                    </template>
                  </v-autocomplete>
                </div>
              </v-col>
            </v-row>
          </div>

          <section class="search-panel-section mt-6" :aria-label="$t('searchUsers')">
            <div class="search-panel">
                <v-row align="center" no-gutters>
                  <v-col cols="12" md="5" class="pa-2">
                    <div class="custom-search-input-wrapper-full">
                      <div class="custom-search-input-container">
                        <v-icon class="custom-search-input-icon">mdi-magnify</v-icon>
                        <input id="username-field-search-setupusergroup" type="text" class="custom-search-input-v2"
                          :aria-label="$t('username')" :placeholder="$t('username')" v-model="username" @keydown.enter="Search" />
                        <v-icon class="clear-icon" :style="username ? '' : 'color: transparent !important;'"
                          @click="username = ''">
                          mdi-close-circle
                        </v-icon>
                      </div>
                    </div>
                  </v-col>
                  <v-col cols="12" md="5" class="pa-2">
                    <div class="custom-search-input-wrapper-full">
                      <div class="custom-search-input-container">
                        <v-icon class="custom-search-input-icon">mdi-magnify</v-icon>
                        <input id="email-field-search-setupusergroup" type="text" class="custom-search-input-v2"
                          :aria-label="$t('email')" :placeholder="$t('email')" v-model="email" @keydown.enter="Search" />
                        <v-icon class="clear-icon" :style="email ? '' : 'color: transparent !important;'"
                          @click="email = ''">
                          mdi-close-circle
                        </v-icon>
                      </div>
                    </div>
                  </v-col>
                  <v-col cols="12" md="2" class="pa-2 d-flex justify-space-between align-center">
                    <div class="d-flex align-center">
                      <div class="d-flex justify-center justify-md-start">
                        <v-btn id="button-search-setupusergroup" class="search-btn-circular" icon size="40" @click="Search"
                          :aria-label="$t('searchUsers')">
                          <v-icon size="22" color="white">mdi-magnify</v-icon>
                          <v-tooltip activator="parent" location="top">
                            {{ $t('searchUsers') }}
                          </v-tooltip>
                        </v-btn>
                      </div>
                      <div class="vertical-divider ml-3"></div>
                    </div>
                    <v-btn id="add-setupusergroup" color="#C30000" variant="elevated" @click="Add" class="action-btn-add"
                      rounded="xl">
                      <v-icon start size="20">mdi-plus</v-icon>
                      {{ $t("add") }}
                    </v-btn>
                  </v-col>
                </v-row>
              </div>
            </section>

          <!-- User Selection Table -->
          <v-data-table-server v-model="userSelected" :items="userList || []" :items-per-page="options.itemsPerPage"
            :items-length="searchTotalTable" item-key="userId" v-model:options="options" :headers="headers" show-select
            :loading="loadingTable" @update:model-value="onSelectionChange" class="user-group-table">
            <!-- Custom header slot for select all checkbox -->
            <template v-slot:header.data-table-select="{ props, on }">
              <v-checkbox-btn v-bind="props" v-on="on"
                :indeterminate="userSelected.length > 0 && userSelected.length < userList.length"
                :model-value="userSelected.length === userList.length && userList.length > 0"
                @update:model-value="toggleSelectAll" class="model-option-checkbox" color="#C30000" :ripple="false" />
            </template>

            <!-- Custom item slot for individual checkboxes -->
            <template v-slot:item.data-table-select="{ item, index }">
              <v-checkbox-btn :model-value="isSelected(item)" @update:model-value="toggleSelect(item, $event)" class="model-option-checkbox" color="#C30000" :ripple="false" />
            </template>
          </v-data-table-server>

        </v-card-text>

        <v-card-actions class="save-actions">
          <div class="actions-right">
            <v-btn variant="outlined" class="setup-cancel-btn" router :to="'UserGroupDetail?id=' + groupID">
              <v-icon class="mr-2" size="25" color="#C30000">mdi-close</v-icon>
              {{ $t("close") }}
            </v-btn>
            <v-btn id="save-setupusergroup" variant="elevated" @click="Save" :loading="dialog" :disabled="!groupname"
              class="save-btn">
              <v-img :src="images.saveImg" alt="Save" class="image-icon-style"></v-img>
              {{ $t("save") }}
            </v-btn>
          </div>
        </v-card-actions>
      </v-card>
    </v-container>

    <!-- Loading Overlay -->
    <v-overlay :model-value="dialog" class="align-center justify-center" persistent>
      <v-card class="loading-card" elevation="8">
        <v-card-text class="text-center">
          <v-progress-circular indeterminate size="64" color="primary" class="mb-4"></v-progress-circular>
          <h3 class="loading-title">{{ $t('loading') }}</h3>
          <p class="loading-subtitle">{{ $t('pleaseWait') }}</p>
        </v-card-text>
      </v-card>
    </v-overlay>
  </div>
</template>

<script>
import Cookies from "js-cookie";
import { decryptData, decryptShiftData } from "@/utils/decrypt";
import LogTrace from "@/api/function.js";
import store from "../store";
import basicInfoIcon from "@/assets/Icons/basic_info.svg";
import modelConfigIcon from "@/assets/Icons/ai-duotone.svg";
import saveImg from "@/assets/Icons/white-save-solid.svg";

export default {
  name: "SetupUserGroup",
  inject: ["flagNav", "FontColorTitle", "SubNavbarBackGround"],
  mixins: [LogTrace],
  data: () => ({
    isInitialized: false,
    show1: false,
    formHasErrors: false,
    permission: {
      add: true,
      edit: true,
      delete: true,
    },
    mailcheck: false,
    phonecheck: false,
    filters: {
      username: [],
      email: [],
      employeeName: [],
      userType: [],
    },
    DDRole: [
      {
        name: "Admin",
        value: 1,
      },
      {
        name: "ผู้ใช้งานทั่วไป",
        value: 2,
      },
    ],
    numberRules: [(v) => !!v || "Input is required!"],
    rules: {
      required: (value) => !!value || "Required.",
      min: (v) => v.length >= 10 || "Min 10 numbers",
    },
    Email: null,
    gg: null,
    UserType: null,
    dialog: false,
    edit: {
      id: null,
      username: null,
      firstname: null,
      lastname: null,
      email: null,
      phone: null,
      password: null,
      roleId: null,
      userType: null,
      model: [],
    },
    user: {
      id: null,
      username: null,
      firstname: null,
      lastname: null,
      email: null,
      phone: null,
      password: null,
      roleId: 2,
      userType: null,
      model: [],
    },
    listDDModel: [],
    oldPassword: "",
    Editdialog: false,
    Adddialog: false,
    isSearch: false,
    username: "",
    email: "",
    totalTable: 0,
    itemsPerPage: 10,
    options: {},
    today: null,
    userListDDL: [],
    userList: [],
    secretKey: "01234567890123456789012345678901",
    roleId: Cookies.get("roleId"),
    isFirst: true,
    searchTotalTable: 0,
    sortBy: [],
    sortDesc: [],
    fileImportUser: null,
    flagTemplate: false,
    groupID: null,
    listDDGroupModel: [],
    DDQuickModel: null,
    searchGroupModel: null,
    groupname: null,
    groupObjectID: null,
    groupModelID: null,
    modelDisplay: null,
    userSelected: [],
    loadingTable: false,
    images: {
      basicInfoIcon: basicInfoIcon,
      modelConfigIcon: modelConfigIcon,
      saveImg: saveImg,
    },
  }),

  watch: {
    options: {
      handler() {
        if (!this.isInitialized) return;
        this.Get();
      },
      deep: true,
    },
    searchGroupModel(val) {
      this.GetGroupModel(val);
    },
  },

  beforeUnmount() {
    store.state.groupSecurityID = null;
  },

  mounted() {
    this.dialog = true;
    this.groupID = this.$route.query.id;
    store.state.groupSecurityID = this.$route.query.id;

    if (this.groupID != null && this.groupID != undefined && this.groupID != "") {
      Promise.all([
        this.GetDate(),
        this.GetGroupModel(),
        this.GetModel(),
        this.GetSetupByID(),
      ]).then(() => {
        this.dialog = false;
        this.Get();
        this.isInitialized = true;
      });
    } else {
      this.$router.push({
        name: "GroupUserManagement",
      });
    }
  },

  computed: {
    headers() {
      if (this.groupID == null || this.groupID == undefined || this.groupID == "") return [];
      return [
        { title: this.$t("no"), align: "center", sortable: false, key: "number" },
        { title: this.$t("username"), align: "start", sortable: true, key: "username" },
        { title: this.$t("email"), align: "start", sortable: true, key: "email" },
        { title: this.$t("name"), align: "start", sortable: true, key: "fullName" },
      ];
    },
    filteredUsers() {
      return this.userList.filter((d) => {
        return Object.keys(this.filters).every((f) => {
          return this.filters[f].length < 1 || this.filters[f].includes(d[f]);
        });
      });
    },
    form() {
      return {
        username: this.user.username,
        firstname: this.user.firstname,
        lastname: this.user.lastname,
        email: this.user.email,
        phone: this.user.phone,
        password: this.user.password,
      };
    },
    headersTemplate() {
      return [
        { text: "CSV", align: "center" },
        { text: "Excel", align: "center" },
      ];
    },
  },

  methods: {
    isModelSelected(modelDisplayID) {
      return Array.isArray(this.modelDisplay) && this.modelDisplay.includes(modelDisplayID);
    },

    removeModel(modelDisplayID) {
      if (this.isAllModelsSelected(this.modelDisplay)) {
        this.modelDisplay = [];
      } else if (Array.isArray(this.modelDisplay)) {
        this.modelDisplay = this.modelDisplay.filter(id => id !== modelDisplayID);
      }
    },

    toggleAllModels() {
      const allIds = (this.listDDModel || []).map((model) => model.modelDisplayID);
      if (!allIds.length) {
        return;
      }
      if (this.isAllModelsSelected(this.modelDisplay)) {
        this.modelDisplay = [];
      } else {
        this.modelDisplay = allIds.slice();
      }
    },

    isAllModelsSelected(selectedModels) {
      const allIds = (this.listDDModel || []).map((model) => model.modelDisplayID);
      if (!Array.isArray(selectedModels) || selectedModels.length === 0) {
        return false;
      }
      if (selectedModels.length !== allIds.length) {
        return false;
      }
      return allIds.every((id) => selectedModels.includes(id));
    },

    shouldCollapseModelChips(selectedModels) {
      return this.isAllModelsSelected(selectedModels);
    },

    onSelectionChange(selected) {
      this.userSelected = selected;
    },

    isSelected(item) {
      return this.userSelected.some(selected => selected.userId === item.userId);
    },

    toggleSelect(item, checked) {
      if (checked) {
        if (!this.isSelected(item)) {
          this.userSelected.push(item);
        }
      } else {
        this.userSelected = this.userSelected.filter(selected => selected.userId !== item.userId);
      }
    },

    toggleSelectAll(checked) {
      if (checked) {
        this.userSelected = [...this.userList];
      } else {
        this.userSelected = [];
      }
    },
    GetGroupModel(searchText = '') {
      this.$call.GetDDGroupModel(
        (dataResponse) => {
          if (dataResponse.status == 0) {
            this.listDDGroupModel = dataResponse.data || [];
          } else {
            this.listDDGroupModel = [];
          }
        },
        (errorResource) => {
          console.error(errorResource);
          this.listDDGroupModel = [];
        }
      );
    },


    validateEmail(email) {
      // eslint-disable-next-line no-useless-escape
      let re = /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/; //NOSONAR
      return re.test(email) || "invalid email";
    },

    Search() {
      this.isSearch = true;
      this.options.page = 1;
      this.Get();
    },


    GetModel() {
      this.$call.DDGPTModel(
        (dataResponse) => {
          if (dataResponse.status == 0) {
            this.listDDModel = dataResponse.data || [];
          } else {
            this.listDDModel = [];
          }
        },
        (errorResource) => {
          console.error(errorResource);
          this.listDDModel = [];
        }
      );
    },

    Get() {
      try {
        this.dialog = true;
        this.loadingTable = true;

        let { page, itemsPerPage, sortBy } = this.options;
        let sortField = null;
        let sortOrder = null;

        if (sortBy && sortBy.length > 0) {
          sortField = sortBy[0].key;
          sortOrder = sortBy[0].order === 'desc';
        }

        let temp = {
          page: this.isSearch ? 1 : page,
          perPage: itemsPerPage == -1 ? this.totalTable : itemsPerPage,
          username: this.username,
          eMail: this.email,
          sortDesc: sortOrder,
          sortBy: sortField,
          groupID: this.groupID,
        };

        this.$call.GetTableSetupUserGroupManagement(
          temp,
          (dataResponse) => {
            if (dataResponse.status == 0) {
              this.searchTotalTable = dataResponse.data.total;
              this.userList = dataResponse.data.data || [];
              this.userListDDL = dataResponse.data.data || [];
              this.totalTable = this.isFirst ? dataResponse.data.total : this.totalTable;
            } else {
              this.userList = [];
              this.totalTable = 0;
              this.searchTotalTable = 0;
            }
            this.dialog = false;
            this.loadingTable = false;
            this.isSearch = false;
            this.isFirst = false;
          },
          (errorResource) => {
            this.dialog = false;
            this.loadingTable = false;
            this.isSearch = false;
            this.userList = [];
            this.totalTable = 0;
            this.searchTotalTable = 0;
            console.error(errorResource);
          }
        );
      } catch (error) {
        this.LogTrace(error, "Get");
      }
    },

    GetSetupByID() {
      this.$call.GetSetupByID(this.groupID, (dataResponse) => {
        if (dataResponse && dataResponse.data) {
          this.groupname = dataResponse.data.groupName;
          this.groupObjectID = dataResponse.data.groupObject_ID;
          this.groupModelID = dataResponse.data.groupModelID;
          this.modelDisplay = dataResponse.data.modelDisplayID;
        }
      });
    },

    Add() {
      this.dialog = true;
      if (this.userSelected != null && this.userSelected.length > 0) {
        let ID = this.userSelected.map((i) => {
          return i.userId;
        });
        let data = {
          ID,
          GroupID: this.groupID,
        };
        this.$call.AddUserIntoGroup(
          data,
          (dataResponse) => {
            this.dialog = false;
            if (dataResponse.status == 0) {
              this.Get();
              this.$swal.fire({
                title: this.$t("_usersAddedSuccess"),
                showConfirmButton: false,
                timer: 1500,
                customClass: {
                  popup: 'success-banner',
                  title: 'success-banner-title'
                },
              });
              this.userSelected = [];
            } else {
              this.$swal.fire({
                icon: "warning",
                title: dataResponse.data,
                showConfirmButton: false,
                timer: 1500,
              });
            }
          },
          (err) => {
            this.dialog = false;
            this.$swal.fire({
              icon: "error",
              title: err,
              showConfirmButton: false,
              timer: 1500,
            });
            console.log(err);
          }
        );
      } else {
        this.$swal.fire({
          icon: "warning",
          title: this.$t("_pleaseSelectUserToAdd"),
          showConfirmButton: false,
          timer: 1500,
        });
        this.dialog = false;
      }
    },

    Save() {
      if (
        this.groupname != null &&
        this.groupname != "" &&
        ((this.groupModelID != null && this.groupModelID != "") ||
          (this.modelDisplay != null && this.modelDisplay != ""))
      ) {
        this.dialog = true;
        let groupID = this.groupID;
        let temp = {
          id: groupID,
          GroupName: this.groupname,
          GroupObject_ID: this.groupObjectID,
          GroupModelID: this.groupModelID,
          ModelDisplayID:
            this.modelDisplay == null ? null : JSON.stringify(this.modelDisplay),
        };
        this.$call.UpdateUserGroup(
          temp,
          (dataResponse) => {
            // ผู้ใช้ที่ติ๊กไว้ในตารางต้องถูกเพิ่มเข้ากลุ่มด้วย ก่อน sync EntraID (sync จะตั้ง SyncStatus = InProcess ซึ่งทำให้ AddUserIntoGroup ไม่เพิ่ม user)
            if (dataResponse.data == "Successfully" && this.userSelected.length > 0) {
              this.AddSelectedUsersOnSave(() => this.HandleUpdateUserGroupResponse(dataResponse));
            } else {
              this.HandleUpdateUserGroupResponse(dataResponse);
            }
          },
          (err) => {
            this.dialog = false;
            this.$swal.fire({
              icon: "error",
              title: err,
              showConfirmButton: false,
              timer: 1500,
            });
            console.log(err);
          }
        );
      } else if (
        this.groupname == null ||
        (this.groupname == "" &&
          (this.groupModelID == null ||
            this.groupModelID == "" ||
            this.modelDisplay == null ||
            this.modelDisplay == ""))
      ) {
        this.$swal.fire({
          icon: "warning",
          title: this.$t("_pleaseFillRequiredFields"),
          showConfirmButton: false,
          timer: 1500,
        });
      } else if (
        this.groupModelID == null ||
        this.groupModelID == "" ||
        this.modelDisplay == null ||
        this.modelDisplay == ""
      ) {
        this.$swal.fire({
          icon: "warning",
          title: this.$t("_pleaseSelectYourModel"),
          showConfirmButton: false,
          timer: 1500,
        });
      }
    },

    HandleUpdateUserGroupResponse(dataResponse) {
      this.dialog = false;
      if (dataResponse.data == "Successfully" && this.groupObjectID != null) {
        this.$call.SyncDataEntraIDGroup(
          this.groupID,
          (response) => {
            this.$swal.fire({
              title: this.$t("_successfullySaved"),
              showConfirmButton: false,
              timer: 1500,
              customClass: {
                popup: 'success-banner',
                title: 'success-banner-title'
              },
            });
            this.GoToUserGroupDetail();
          },
          (error) => {
            this.dialog = false;
            this.$swal.fire({
              icon: "error",
              title: error,
              showConfirmButton: false,
              timer: 1500,
            });
            console.log(error);
          }
        );
      } else if (dataResponse.data == "Successfully") {
        this.$swal.fire({
          title: this.$t("_successfullySaved"),
          showConfirmButton: false,
          timer: 1500,
          customClass: {
            popup: 'success-banner',
            title: 'success-banner-title'
          },
        });
        this.GoToUserGroupDetail();
      } else {
        this.$swal.fire({
          icon: "warning",
          title: dataResponse.data,
          showConfirmButton: false,
          timer: 1500,
        });
      }
    },

    AddSelectedUsersOnSave(onSuccess) {
      let data = {
        ID: this.userSelected.map((i) => i.userId),
        GroupID: this.groupID,
      };
      this.$call.AddUserIntoGroup(
        data,
        (dataResponse) => {
          if (dataResponse.status == 0) {
            this.userSelected = [];
            this.Get();
            onSuccess();
          } else {
            this.dialog = false;
            this.$swal.fire({
              icon: "warning",
              title: dataResponse.data,
              showConfirmButton: false,
              timer: 1500,
            });
          }
        },
        (err) => {
          this.dialog = false;
          this.$swal.fire({
            icon: "error",
            title: err,
            showConfirmButton: false,
            timer: 1500,
          });
          console.log(err);
        }
      );
    },

    GoToUserGroupDetail() {
      this.$router.push({
        name: "UserGroupDetail",
        query: {
          id: this.groupID,
        },
      });
    },

    Sync() {
      this.dialog = true;
      this.$call.SyncDataEntraIDGroup(
        this.groupID,
        (dataResponse) => {
          if (dataResponse.status == 0) {
            this.$swal
              .fire({
                icon: "success",
                title: dataResponse.message,
                showConfirmButton: false,
                timer: 1500,
              })
              .then(() => {
                this.Get();
              });
          } else {
            this.$swal.fire({
              icon: "warning",
              title: dataResponse.data,
              confirmButtonColor: "#3085d6",
              confirmButtonText: this.$t("close"),
            });
          }
          this.dialog = false;
        },
        (err) => {
          console.log(err);
          this.dialog = false;
        }
      );
    },

    decryptData(encryptedText, key) {
      return decryptData(encryptedText, key);
    },

    decryptShiftData(encryptedText, shift) {
      return decryptShiftData(encryptedText, shift);
    },
  },
};
</script>

<style scoped>
/* Base Styles */
.title {
  color: white;
}

.setup-header {
  padding: 16px 0;
}

.header-text-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-left: 24px;
}

.setup-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #171717;
  margin: 0;
  letter-spacing: -0.025em;
}

.setup-subtitle {
  font-size: 0.875rem;
  color: #6B7280;
  margin: 0;
  font-weight: 400;
}

/* Form Card */
.setup-form-card {
  border-radius: 16px !important;
  background: white !important;
  border: 1px solid #E5E7EB !important;
  margin-bottom: 16px;
  padding: 16px 0px !important;
}

/* Form Content */
.form-content {
  padding: 10px 20px !important;
  background: white;
}

/* Border Section Wrapper - matching GroupUserManagement */
.border-section-wrapper {
  border-radius: 24px;
  padding: 15px 20px;
  border: 1px solid #CCCCCC;
  transition: all 0.3s ease;
}

.basic-info-container {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
}

.basic-info-icon {
  width: 24px;
  height: 24px;
}

.section-content {
  margin-top: 5px;
}

.section-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #2c3e50;
  margin: 0;
  letter-spacing: 0.025em;
}

/* Form Fields */
.form-field {
  margin-bottom: 8px;
}

.input-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #171717;
  line-height: 1.2;
  display: flex;
  align-items: center;
  gap: 6px;
}

.required-asterisk {
  color: #C30000;
  font-weight: 700;
}

.info-icon {
  transition: all 0.2s ease;
  opacity: 0.7;
  cursor: pointer;
}

.info-icon:hover {
  opacity: 1;
  transform: scale(1.1);
}

.tooltip-content {
  font-size: 0.875rem;
  line-height: 1.5;
}

.tooltip-content div {
  margin-bottom: 2px;
}

.tooltip-content div:last-child {
  margin-bottom: 0;
}

/* Modern Field Styles */
.modern-field {
  background: white;
  border-radius: 16px !important;
}

.modern-field :deep(.v-field) {
  border-radius: 16px !important;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.modern-field :deep(.v-field__input) {
  padding: 10px 16px !important;
  font-size: 0.95rem;
}

.modern-field :deep(.v-label) {
  font-weight: 500;
  color: #6c757d;
}

.modern-field :deep(.v-field--focused .v-label) {
  color: #171717 !important;
}

/* Model Autocomplete Styles */
.model-autocomplete :deep(.v-chip) {
  border: 1px solid #C30000 !important;
  color: #C30000 !important;
  border-radius: 999px !important;
  font-weight: 500;
  font-size: 0.8125rem;
  padding: 0 4px 0 12px !important;
  background-color: #FFFFFF !important;
}

.model-autocomplete :deep(.v-chip .v-chip__content) {
  color: #C30000 !important;
}

.model-autocomplete :deep(.v-chip .v-chip__close) {
  display: flex;
  align-items: center;
  padding: 0px 8px 0px 4px;
  border-left: 1px solid #646464;
  color: #646464 !important;
  opacity: 0.6;
  font-size: 14px !important;
  width: auto !important;
  height: 100%;
}

.model-autocomplete :deep(.v-chip .v-chip__close:hover) {
  opacity: 1;
}

.model-chip {
  border: 1px solid #C30000 !important;
  color: #C30000 !important;
  background-color: #FFFFFF !important;
  border-radius: 999px !important;
  font-weight: 600;
  letter-spacing: 0.02em;
  margin: 2px 4px 2px 0;
  padding: 0 8px !important;
}

.more-chip {
  color: #C30000 !important;
  border: 1px solid #C30000 !important;
  background-color: #FAEBEB !important;
  font-weight: 600;
}

.model-option-item {
  border-radius: 10px;
  transition: background 0.2s ease, border 0.2s ease;
}

:deep(.model-option-item--selected) {
  background: rgba(195, 0, 0, 0.08) !important;
  border: 1px solid rgba(195, 0, 0, 0.25);
}

:deep(.model-option-checkbox) {
  margin-right: 8px;
}

:deep(.model-option-checkbox .v-selection-control__input) {
  width: 20px;
  height: 20px;
}

:deep(.model-option-title) {
  font-weight: 500;
}

:deep(.model-info-icon) {
  color: rgba(var(--v-theme-on-surface), 0.6);
  cursor: pointer;
  transition: color 0.2s ease;
}

:deep(.model-info-icon:hover) {
  color: #C30000;
}

.model-all-selected-label {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.875rem;
  font-weight: 600;
  color: #C30000;
  padding: 6px 12px;
  border-radius: 16px;
  background-color: #FAEBEB;
  white-space: nowrap;
  flex-shrink: 0;
}

.model-all-divider {
  width: 100% !important;
  border-color: #CCCCCC !important;
  height: 2px !important;
  opacity: 1 !important;
}

:deep(.model-all-option) {
  display: flex !important;
  align-items: center !important;
  gap: 8px !important;
  padding: 10px 14px !important;
  border-radius: 12px !important;
  cursor: pointer !important;
  transition: background 0.2s ease, border 0.2s ease, box-shadow 0.2s ease !important;
  width: 100% !important;
}

:deep(.model-all-option .v-list-item__content) {
  display: flex !important;
  align-items: center !important;
}

:deep(.model-all-title) {
  display: inline-flex !important;
  align-items: center !important;
  gap: 6px !important;
}

:deep(.model-all-option--active) {
  background: rgba(195, 0, 0, 0.08);
  border: 1px solid rgba(195, 0, 0, 0.3);
  box-shadow: 0 2px 8px rgba(195, 0, 0, 0.12);
}

:deep(.model-all-option--disabled) {
  opacity: 0.5;
  cursor: not-allowed;
}

:deep(.model-all-option__label) {
  white-space: nowrap !important;
  font-weight: 600;
  color: rgb(var(--v-theme-on-surface));
}

/* User Table Card */
.user-table-card {
  border-radius: 16px !important;
  background: white !important;
  border: 1px solid #E5E7EB !important;
  margin-bottom: 16px;
  overflow: hidden;
}

.user-group-table {
  padding: 0px !important;
  overflow: hidden;
}

.user-group-table :deep(thead) {
  background-color: #FFF2F2 !important;
}

.user-group-table :deep(thead tr) {
  background-color: #FFF2F2 !important;
}

.user-group-table :deep(thead th) {
  background-color: #FFF2F2 !important;
  color: #171717 !important;
  font-weight: 800 !important;
  font-size: 16px !important;
  letter-spacing: 0.5px;
}

.user-group-table :deep(thead th:first-child) {
  border-top-left-radius: 16px !important;
}

.user-group-table :deep(thead th:last-child) {
  border-top-right-radius: 16px !important;
}

.user-group-table :deep(.v-data-table__th) {
  background-color: #FFF2F2 !important;
  color: #171717 !important;
  font-weight: 800 !important;
}

.search-panel-section {
  width: 100%;
  padding: 10px 15px;
  border-radius: 16px;
  border: 1px solid var(--placeholder, #ccc);
  margin-bottom: 20px;
  background: #ffffff;
}

.search-field :deep(.v-field) {
  border-radius: 12px !important;
  background: #F9FAFB !important;
}

.search-btn {
  border-radius: 12px !important;
}

.save-actions {
  padding: 5px 24px !important;
  background: white !important;
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.actions-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.actions-left {
  display: flex;
  align-items: center;
}

.actions-right {
  display: flex;
  align-items: center !important;
  gap: 12px;
}

.status-chip {
  font-weight: 500;
  letter-spacing: 0.025em;
}

.save-btn {
  height: 44px !important;
  min-width: 120px;
  font-weight: 600 !important;
  border-radius: 12px !important;
  text-transform: none !important;
  background: #03872B !important;
  color: white !important;
}

.save-btn:hover:not(:disabled) {
  transform: translateY(-2px);
}

.save-btn:disabled {
  background: linear-gradient(135deg, #e5a599 0%, #fbc4bb 100%) !important;
  box-shadow: none !important;
}

.image-icon-style {
  width: 20px;
  height: 20px;
  margin-right: 8px;
}

/* Loading Card */
.loading-card {
  border-radius: 16px !important;
  padding: 32px;
  background: white !important;
}

.loading-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #171717;
  margin: 0;
}

.loading-subtitle {
  font-size: 0.875rem;
  color: #6B7280;
  margin: 0;
}

/* Data Table Row Styles */
:deep(.v-data-table tbody tr) {
  transition: all 0.2s ease;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

:deep(.v-data-table tbody tr:hover) {
  background: rgba(0, 0, 0, 0.02);
}

/* Responsive Design */
@media (max-width: 960px) {
  .search-panel-section {
    padding: 16px;
  }

  .form-content {
    padding: 16px !important;
  }

  .border-section-wrapper {
    padding: 12px 16px;
  }

  .actions-content {
    flex-direction: column-reverse;
    gap: 12px;
    align-items: stretch;
  }

  .actions-right {
    justify-content: center;
  }

  .actions-left {
    justify-content: center;
  }
}

@media (max-width: 600px) {
  .setup-title {
    font-size: 1.25rem;
  }

  .section-title {
    font-size: 1rem;
  }

  .modern-field :deep(.v-field__input) {
    padding: 14px;
    font-size: 0.9rem;
  }

  .save-btn {
    width: 100%;
  }
}

/* Dark Theme Support */
.v-theme--dark .setup-form-card,
.v-theme--dark .user-table-card,
.v-theme--dark .save-actions-card {
  background: #1e1e1e !important;
  border-color: #404040 !important;
}

.v-theme--dark .form-content,
.v-theme--dark .search-panel,
.v-theme--dark .save-actions {
  background: #1e1e1e;
}

.v-theme--dark .setup-title {
  color: #e0e0e0;
}

.v-theme--dark .setup-subtitle {
  color: #9CA3AF;
}

.v-theme--dark .input-label {
  color: #e0e0e0;
}

.v-theme--dark .section-title {
  color: #e0e0e0;
}

.v-theme--dark .border-section-wrapper {
  border-color: #404040;
}

.v-theme--dark .modern-field :deep(.v-field) {
  background: #2d2d2d;
  border-color: #404040;
}

.v-theme--dark .user-group-table :deep(thead),
.v-theme--dark .user-group-table :deep(thead tr),
.v-theme--dark .user-group-table :deep(thead th),
.v-theme--dark .user-group-table :deep(.v-data-table__th) {
  background-color: #2d2d2d !important;
  color: #e0e0e0 !important;
}

.v-theme--dark .loading-card {
  background: #2d2d2d !important;
}

.v-theme--dark .loading-title {
  color: #e0e0e0;
}

.v-theme--dark .loading-subtitle {
  color: #9CA3AF;
}
.setup-cancel-btn {
  min-height: 42px;
  min-width: 120px;
  border-radius: 12px;
  color: #C30000;
  text-transform: none;
  border: 1px solid #C30000;
  background: transparent !important;
}
.custom-search-input-icon {
  color: #872612;
  margin-right: 10px;
  font-size: 24px;
}
.search-btn-circular {
  background: linear-gradient(90deg, #c32e0e 0%, #f67760 100%) !important;
  box-shadow: 0 4px 12px rgba(195, 46, 14, 0.3) !important;
  transition: all 0.2s ease;
}

.search-btn-circular:hover {
  box-shadow: 0 6px 16px rgba(195, 46, 14, 0.4) !important;
  transform: scale(1.05);
}
.vertical-divider {
  width: 1px;
  height: 40px;
  background-color: #ccc;
}
.action-btn-add {
  border-radius: 16px !important;
  background: linear-gradient(90deg, #C32E0E 0%, #F67760 100%) !important;
  color: white !important;
  font-weight: 600;
  text-transform: none;
  padding: 0 24px !important;
  height: 42px !important;
  box-shadow: 0 4px 12px rgba(243, 98, 67, 0.3) !important;
}

.action-btn-add:hover {
  background: linear-gradient(90deg, #F36243 0%, #FF8E7A 100%) !important;
  box-shadow: 0 6px 16px rgba(243, 98, 67, 0.4) !important;
  transform: translateY(-1px);
}
</style>